import { useState } from 'react';
import { Box, Flex, Text, UnstyledButton } from '@mantine/core';
import type { CalendarEvent } from 'src/meetings/models';
import { MeetingNoteTab } from 'src/meetings/detail/tabs/MeetingNoteTab';
import { PreMeetingTab } from 'src/meetings/detail/tabs/PreMeetingTab';
import { SummaryTab } from 'src/meetings/detail/tabs/SummaryTab';
import { TranscriptTab } from 'src/meetings/detail/tabs/TranscriptTab';
import { type Transcript, TranscriptionStatus } from 'src/meetings/detail/tabs/transcript/types';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type TabValue = 'summary' | 'meeting-note' | 'transcript' | 'pre-meeting';

const TABS: { value: TabValue; label: string }[] = [
  { value: 'summary', label: 'Summary' },
  { value: 'meeting-note', label: 'Meeting note' },
  { value: 'transcript', label: 'Transcript' },
  { value: 'pre-meeting', label: 'Pre-meeting papers' },
];

type Props = {
  meeting: CalendarEvent;
  transcript: Transcript | undefined;
};

export function PastMeetingTabs({ meeting, transcript }: Props) {
  const [tab, setTab] = useState<TabValue>('summary');

  return (
    <Box>
      <Flex gap="xxxs">
        {TABS.map(({ value, label }) => {
          const isActive = tab === value;
          return (
            <UnstyledButton
              key={value}
              onClick={() => setTab(value)}
              bg={isActive ? 'brand.1' : 'white'}
              py="xxs"
              px="xs"
              style={{ borderRadius: 6 }}
            >
              <Text size="textSm" fw={FONT_WEIGHTS.semibold} c={isActive ? 'brand.8' : 'gray.6'}>
                {label}
              </Text>
            </UnstyledButton>
          );
        })}
      </Flex>

      <Box mt="xl">{renderTab(tab, meeting, transcript)}</Box>
    </Box>
  );
}

function renderTab(tab: TabValue, meeting: CalendarEvent, transcript: Transcript | undefined) {
  if (tab === 'meeting-note') return <MeetingNoteTab event={meeting} />;
  if (tab === 'pre-meeting') return <PreMeetingTab event={meeting} />;

  if (!transcript || transcript.status !== TranscriptionStatus.READY) {
    const message =
      transcript?.status === TranscriptionStatus.PROCESSING
        ? "Your recording is being transcribed. You'll be able to use this tab once it's ready."
        : 'No transcript is available for this meeting.';
    return (
      <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.6">
        {message}
      </Text>
    );
  }

  return tab === 'summary' ? (
    <SummaryTab event={meeting} transcript={transcript} hideSuggestedActions />
  ) : (
    <TranscriptTab event={meeting} transcript={transcript} inlineAudioPlayer />
  );
}
